import React, { createContext, useContext, useMemo, useState } from "react";
import { useLoaderData } from "react-router";

import {
    ScheduleActionsContextProps,
    ScheduleActionsContextValues,
} from "../types/contexts";
import { ShowComponents } from "../types/scheduleactions";

import { IMasterTournamentSchedule } from "../../../../../types/api";
import { AsyncStatuses } from "../../../../../types/shared";

const ScheduleActionsContext =
    createContext<ScheduleActionsContextValues | null>(null);

export const useScheduleActionsContext = () => {
    const context = useContext(ScheduleActionsContext);
    if (!context) {
        throw new Error(
            "useScheduleActionsContext must be used within a ScheduleActionsProvider",
        );
    }
    return context;
};

export const ScheduleActionsProvider: React.FC<
    ScheduleActionsContextProps
> = props => {
    const { children } = props;

    const { schedule: loadedSchedule } = useLoaderData() as {
        schedule: IMasterTournamentSchedule;
    };

    const [schedule, setSchedule] =
        useState<IMasterTournamentSchedule>(loadedSchedule);
    const [showComponent, setShowComponent] = useState<ShowComponents>({
        moveGames: false,
        adjustStarts: false,
        adjustLengths: false,
    });

    const [fetchStatus, setFetchStatus] = useState<AsyncStatuses>(undefined);

    const [moveGamesFromField, setMoveGamesFromField] = useState<number>();
    const [moveGamesToField, setMoveGamesToField] = useState<number>();
    const [daysToMove, setDaysToMove] = useState<string[]>([]);
    const [moveGamesDays, setMoveGamesDays] = useState<string[]>([]);

    const [adjustStartTimesGames, setAdjustStartTimesGames] = useState<
        number[]
    >([]);
    const [adjustStartTimesDays, setAdjustStartTimesDays] = useState<string[]>(
        [],
    );
    const [adjustStartTimesDayToAdjust, setAdjustStartTimesDayToAdjust] =
        useState<string | undefined>();
    const [adjustStartTimesField, setAdjustStartTimesField] = useState<
        number | undefined
    >();
    const [adjustStartTimesAmount, setAdjustStartTimesAmount] = useState<
        string | undefined
    >();

    const [adjustGameLengthsGames, setAdjustGameLengthsGames] = useState<
        number[]
    >([]);
    const [adjustGameLengthsDays, setAdjustGameLengthsDays] = useState<
        string[]
    >([]);
    const [adjustGameLengthsDayToAdjust, setAdjustGameLengthsDayToAdjust] =
        useState<string | undefined>();
    const [adjustGameLengthsField, setAdjustGameLengthsField] = useState<
        number | undefined
    >();
    const [adjustGameLengthsAmount, setAdjustGameLengthsAmount] = useState<
        string | undefined
    >();

    const contextValue = useMemo(
        () => ({
            schedule,
            setSchedule,
            showComponent,
            setShowComponent,
            fetchStatus,
            setFetchStatus,
            moveGamesFromField,
            setMoveGamesFromField,
            moveGamesToField,
            setMoveGamesToField,
            daysToMove,
            setDaysToMove,
            moveGamesDays,
            setMoveGamesDays,
            adjustStartTimesGames,
            setAdjustStartTimesGames,
            adjustStartTimesDays,
            setAdjustStartTimesDays,
            adjustStartTimesDayToAdjust,
            setAdjustStartTimesDayToAdjust,
            adjustStartTimesField,
            setAdjustStartTimesField,
            adjustStartTimesAmount,
            setAdjustStartTimesAmount,
            adjustGameLengthsGames,
            setAdjustGameLengthsGames,
            adjustGameLengthsDays,
            setAdjustGameLengthsDays,
            adjustGameLengthsDayToAdjust,
            setAdjustGameLengthsDayToAdjust,
            adjustGameLengthsField,
            setAdjustGameLengthsField,
            adjustGameLengthsAmount,
            setAdjustGameLengthsAmount,
        }),
        [
            showComponent,
            schedule,
            fetchStatus,
            daysToMove,
            moveGamesFromField,
            moveGamesToField,
            moveGamesDays,
            adjustStartTimesGames,
            adjustStartTimesDays,
            adjustStartTimesDayToAdjust,
            adjustStartTimesField,
            adjustStartTimesAmount,
            adjustGameLengthsGames,
            adjustGameLengthsDays,
            adjustGameLengthsDayToAdjust,
            adjustGameLengthsField,
            adjustGameLengthsAmount,
        ],
    );

    return (
        <ScheduleActionsContext.Provider value={contextValue}>
            {children}
        </ScheduleActionsContext.Provider>
    );
};
